import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Chip, IconButton, Paper, TextField, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getAllTransactions, getAllTransactionsBySeller, cancelInvoice } from '../../services/SalesService';
import SalesReturnModal from './SalesReturnModal';
import { formatDateTime } from '../../utils/DateUtils';
import { getPaymentLabel, getOriginLabel, getInvoiceStatus, getInvoiceStatusColor, getPaymentStatusLabel, getPaymentStatusColor } from '../../utils/Enums';
import { getIcon } from '../../utils/IconHandler';
import { getProfile } from '../../services/UserService';
import ConfirmCancelInvoiceModal from './ConfirmCancelInvoiceModal';
import TransactionDetailModal from './TransactionDetailModal';
import ViewFiscalTicketModal from './../../components/ElectronicBilling/ViewFiscalTicketModal';
import TableRefreshButton from '../TableRefreshButton/TableRefreshButton';
import PlatformBridge from '../../utils/PlatformBridge';
import { getStoredPermissions } from '../../utils/dashboardPermissions';
import { canManageSalesReturns } from '../../utils/salesReturnAccess';
import { formatMoney } from '../../utils/currency';

const formatDiscountPercent = (discountAmount, subtotal) => {
  const safeDiscount = Number(discountAmount ?? 0);
  const safeSubtotal = Number(subtotal ?? 0);

  if (safeDiscount <= 0 || safeSubtotal <= 0) {
    return '—';
  }

  const percent = (safeDiscount / safeSubtotal) * 100;
  return `${percent.toFixed(2)}%`;
};

const handleTicketOutput = async (base64, viewPrint, autoPrint) => {
  if (!base64 || (!viewPrint && !autoPrint)) return;

  if (viewPrint) {
    PlatformBridge.openPdf(base64);
  }

  if (!autoPrint) return;

  if (PlatformBridge.isDesktop()) {
    await PlatformBridge.printPdf(base64);
    return;
  }

  if (!viewPrint) {
    PlatformBridge.openPdf(base64);
  }
};

const TransactionTable = ({
  bySeller = false,
  locationId,
  terminalCode,
  showCancelFor = 'last',
  preferredRut = null,
  enableAccountingFallback = false,
}) => {
  const { t } = useTranslation();
  const storedRole = localStorage.getItem('role');
  const [canReturnSales, setCanReturnSales] = useState(() => canManageSalesReturns(getStoredPermissions()));
  const [isFranchiseManager, setIsFranchiseManager] = useState(
    storedRole === 'FRANCHISE_MANAGER' || storedRole === 'Franquiciante'
  );

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState([]);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [totalRows, setTotalRows] = useState(0);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedTicketContext, setSelectedTicketContext] = useState(null);
  const [lastInvoiceId, setLastInvoiceId] = useState(null);

  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [invoiceIdToCancel, setInvoiceIdToCancel] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [returnInvoiceId, setReturnInvoiceId] = useState(null);

  // Ref para manejar debounce
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    getProfile()
      .then((profile) => {
        if (!isMounted) return;
        const templateCode = profile?.role?.templateCode;
        const roleName = profile?.role?.name;
        setIsFranchiseManager(templateCode === 'FRANCHISE_MANAGER' || roleName === 'Franquiciante');
        setCanReturnSales(canManageSalesReturns(profile?.permissions ?? getStoredPermissions()));
      })
      .catch(() => {
        if (!isMounted) return;
        setIsFranchiseManager(storedRole === 'FRANCHISE_MANAGER' || storedRole === 'Franquiciante');
        setCanReturnSales(canManageSalesReturns(getStoredPermissions()));
      });

    return () => {
      isMounted = false;
    };
  }, [storedRole]);

  const fetchTransactions = useCallback(async (page, pageSize, search, currentSortModel = sortModel, currentFilterModel = filterModel) => {
    try {
      setLoading(true);
      const cleanSearch = search || '';
      const activeSort = Array.isArray(currentSortModel) ? currentSortModel[0] : null;
      const activeFilter = currentFilterModel?.items?.find((item) => item?.field && item?.operator);
      const requestOptions = {
        sortField: activeSort?.field,
        sortDirection: activeSort?.sort,
        filterField: activeFilter?.field,
        filterOperator: activeFilter?.operator,
        filterValue: activeFilter?.value,
      };
      const data = bySeller
        ? await getAllTransactionsBySeller(locationId, terminalCode, page, pageSize, cleanSearch, requestOptions)
        : await getAllTransactions(page, pageSize, cleanSearch, requestOptions);

      // El backend devuelve { transactions: [...], total: number, page: number, pageSize: number }
      if (data && typeof data === 'object') {
        setTransactions(data.transactions || data.data || []);
        setTotalRows(data.total || data.totalCount || 0);
      } else {
        // Fallback para compatibilidad con respuesta anterior
        setTransactions(data || []);
        setTotalRows(data?.length || 0);
      }

      // Determinar la última factura por fecha
      const transactionsData = data.transactions || data.data || data || [];
      if (transactionsData.length > 0) {
        const sorted = [...transactionsData].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        setLastInvoiceId(sorted[0].id);
      }
    } catch (e) {
      console.error('Error fetching transactions', e);
      setTransactions([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [bySeller, locationId, terminalCode, sortModel, filterModel]);

  // Cargar datos iniciales
  useEffect(() => {
    fetchTransactions(paginationModel.page + 1, paginationModel.pageSize, searchTerm);
    
    // Cleanup timeout al desmontar
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [fetchTransactions, paginationModel.page, paginationModel.pageSize, sortModel, filterModel]);

  // Funciones para manejar cambios de paginación
  const handlePaginationChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleFilterModelChange = (newFilterModel) => {
    const normalizedItems = (newFilterModel?.items || [])
      .filter((item) => item?.field && item?.operator)
      .slice(0, 1);
    setFilterModel({ ...newFilterModel, items: normalizedItems });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  // Función para manejar cambios de búsqueda con debounce
  const handleSearchChange = (searchTerm) => {
    setSearchTerm(searchTerm);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    
    // Limpiar timeout anterior
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce la búsqueda
    searchTimeoutRef.current = setTimeout(() => {
      fetchTransactions(1, paginationModel.pageSize, searchTerm, sortModel, filterModel);
    }, 500);
  };

  // Ya no necesitamos filteredTransactions porque el filtrado se hace en el servidor
  
  const baseColumns = [
    {
      field: 'createdAt',
      headerName: 'Fecha',
      flex: 1.3,
      valueFormatter: (params) => formatDateTime(params),
    },
    {
      field: 'discountAmount',
      headerName: 'Descuento',
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => formatDiscountPercent(params.row.discountAmount, params.row.subTotal),
    },
    {
      field: 'total',
      headerName: 'Total',
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (params, row) =>
        typeof params === 'number' ? formatMoney(params, row?.currency) : '—',
    },
    {
      field: 'origin',
      headerName: 'Origen',
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (params) => getOriginLabel(params),
    },
    {
      field: 'paymentMethod',
      headerName: 'Método',
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (params) => getPaymentLabel(params),
    },
    {
      field: 'paymentStatus',
      headerName: 'Pago',
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <span style={{ color: getPaymentStatusColor(params.value), fontWeight: 'bold' }}>
          {getPaymentStatusLabel(params.value)}
        </span>
      )
    },
    {
      field: 'status',
      headerName: 'Estado',
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
          <span style={{ color: getInvoiceStatusColor(params.value), fontWeight: 'bold' }}>
            {getInvoiceStatus(params.value)}
          </span>
          {params.row.electronicEmissionPending && (
            <Tooltip title={t('electronic_invoice_pending_tooltip')}>
              <Chip label={t('electronic_invoice_pending_label')} size="small" color="warning" variant="outlined" />
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      flex: 1.7,
      // Hasta cinco acciones por fila (ver, factura, nota de crédito, devolver, anular): con menos ancho se pisan.
      minWidth: 250,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const isCancelled = getInvoiceStatus(params.row.status) === 'Canceled';
        const hasCreditNote = isCancelled || params.row.hasReturn === true;
        const isLastInvoice = params.row.id === lastInvoiceId;
      
        const shouldShowCancelButton = (() => {
          const effectiveShowCancelFor = isFranchiseManager ? 'all' : showCancelFor;

          if (effectiveShowCancelFor === 'all') {
            return !isCancelled;
          }
          if (effectiveShowCancelFor === 'last') {
            return isLastInvoice && !isCancelled;
          }
          return false;
        })();
      
        return (
          <>
            <IconButton
              onClick={() => {
                setSelectedTransaction(params.row);
                setModalOpen(true);
              }}
            >
              {getIcon('eye')}
            </IconButton>
      
            <Tooltip title="Ver factura original">
              <IconButton
                onClick={() => {
                  // Este acceso siempre conserva el comprobante original, aun si existe una NC.
                  setSelectedTicketContext({
                    invoiceId: params.row.id,
                    externalDocumentId: params.row.externalDocumentId ?? null,
                    rut: preferredRut ?? params.row.rut ?? params.row.companyRut ?? null,
                    preferCreditNote: false,
                  });
                  setViewModalOpen(true);
                }}
              >
                {getIcon('reader')}
              </IconButton>
            </Tooltip>

            {hasCreditNote && (
              <Tooltip title="Ver nota de crédito">
                <IconButton
                  color="error"
                  onClick={() => {
                    setSelectedTicketContext({
                      invoiceId: params.row.id,
                      externalDocumentId: params.row.externalDocumentId ?? null,
                      rut: preferredRut ?? params.row.rut ?? params.row.companyRut ?? null,
                      preferCreditNote: true,
                    });
                    setViewModalOpen(true);
                  }}
                >
                  {getIcon('creditNote')}
                </IconButton>
              </Tooltip>
            )}

            {canReturnSales && !isCancelled && (
              <Tooltip title="Registrar devolución">
                <IconButton color="warning" onClick={() => setReturnInvoiceId(params.row.id)}>
                  {getIcon('salesReturn')}
                </IconButton>
              </Tooltip>
            )}

            {shouldShowCancelButton && (
              <Tooltip title="Cancelar factura">
                <IconButton
                  color="error"
                  onClick={() => {
                    setInvoiceIdToCancel(params.row.id);
                    setCancelModalOpen(true);
                  }}
                >
                  {getIcon('trash')}
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      }      
    },
  ];  
  
  const fullColumns = [
    { field: 'id', headerName: 'N.º operación', width: 125 },
    ...baseColumns.slice(0, 1),
    {
      field: 'sellerLastName',
      headerName: 'Vendedor',
      flex: 1.1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => `${params.row.sellerName ?? ''} ${params.row.sellerLastName ?? ''}`
    },
    {
      field: 'locationName',
      align: 'center',
      headerAlign: 'center',
      headerName: 'Sucursal',
      flex: 0.8,
    },
    ...baseColumns.slice(1),
  ];

  const handleCancelInvoice = async (invoiceId) => {
    try {
      const result = await cancelInvoice(invoiceId);
      const viewPrint = result?.viewPrint === true;
      const autoPrint = result?.autoPrint === true;
      if (result?.creditNoteFactura) {
        try {
          await handleTicketOutput(result.creditNoteFactura, viewPrint, autoPrint);
        } catch (printError) {
          console.error('Error imprimiendo nota de crédito:', printError);
          alert('La factura fue cancelada y la nota de crédito está lista, pero no se pudo procesar la salida automática del ticket.');
        }
      }
      if (result?.loyaltyCardTicket) {
        try {
          await handleTicketOutput(
            result.loyaltyCardTicket,
            result.viewPrint === true,
            result.autoPrint === true
          );
        } catch (printError) {
          console.error('Error imprimiendo actualización de cartilla:', printError);
          alert('La factura fue cancelada, pero no se pudo procesar la impresión de la cartilla actualizada.');
        }
      } else if (!result?.creditNoteFactura && result?.message) {
        alert(result.message);
      }
      // Refrescar los datos después de cancelar
      fetchTransactions(paginationModel.page + 1, paginationModel.pageSize, searchTerm);
      setSelectedTicketContext({
        invoiceId,
        externalDocumentId: null,
        rut: preferredRut,
        preferCreditNote: true,
      });
      setViewModalOpen(true);
    } catch (e) {
      const backendMessage = e?.response?.data?.error_message || e?.response?.data?.message;
      alert(backendMessage || 'Error al cancelar la factura');
      console.error('Cancel error:', e);
    }
  };  
    
  const columns = bySeller ? [{ field: 'id', headerName: 'N.º operación', width: 125 }, ...baseColumns] : fullColumns;

  return (
    <>
      <Paper sx={{ padding: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <TextField
            label={t('search')}
            placeholder={t('search_by_date_total_seller')}
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            sx={{ width: 300 }}
          />
          <TableRefreshButton
            loading={loading}
            onClick={() => fetchTransactions(paginationModel.page + 1, paginationModel.pageSize, searchTerm)}
          />
        </Box>

        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 1300 }}>
            <DataGrid
              loading={loading}
              rows={transactions}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationChange}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              sortingMode="server"
              filterModel={filterModel}
              onFilterModelChange={handleFilterModelChange}
              filterMode="server"
              pageSizeOptions={[10, 25, 50]}
              paginationMode="server"
              rowCount={totalRows}
              autoHeight
            />
          </Box>
        </Box>
      </Paper>

      {modalOpen && selectedTransaction && (
        <TransactionDetailModal
          open={modalOpen}
          invoiceId={selectedTransaction?.id}
          initialTransaction={selectedTransaction}
          enableAccountingFallback={enableAccountingFallback}
          onClose={() => {
            setModalOpen(false);
            setSelectedTransaction(null);
          }}
        />
      )}

      <ViewFiscalTicketModal
        open={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setSelectedTicketContext(null);
        }}
        invoiceId={selectedTicketContext?.invoiceId}
        fallbackExternalDocumentId={selectedTicketContext?.externalDocumentId}
        fallbackRut={selectedTicketContext?.rut}
        useAccountingFallback={enableAccountingFallback}
        preferCreditNote={selectedTicketContext?.preferCreditNote === true}
        source="treat"
      />

      <ConfirmCancelInvoiceModal
        open={cancelModalOpen}
        loading={cancelLoading}
        onCancel={() => {
          setCancelModalOpen(false);
          setInvoiceIdToCancel(null);
        }}
        onConfirm={async () => {
          if (!invoiceIdToCancel) return;
          try {
            setCancelLoading(true);
            await handleCancelInvoice(invoiceIdToCancel);
            setCancelModalOpen(false);
            setInvoiceIdToCancel(null);
          } catch (error) {
            console.error(error);
          } finally {
            setCancelLoading(false);
          }
        }}
      />

      <SalesReturnModal
        open={Boolean(returnInvoiceId)}
        invoiceId={returnInvoiceId}
        locationId={locationId}
        onClose={() => setReturnInvoiceId(null)}
        onCreated={() => fetchTransactions(paginationModel.page, paginationModel.pageSize, searchTerm)}
      />

    </>
  );
};

export default TransactionTable;
